import { Box } from 'braid-design-system';
import { observer } from 'mobx-react';
import React from 'react';

import { ExpireJobDialog } from 'src/App/AppPageBlock/ExpireJobDialog';
import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';
import type { PageConfig } from 'src/libs/PageConfig';
import {
  trackExpireAdClickEvent,
  trackViewJobNotificationsClickEvent,
  trackViewTeamAccessClickEvent,
} from 'src/utils/events';
import useQueryParamsStore from 'src/utils/hooks/useQueryParamsStore';
import { useStores } from 'src/utils/hooks/useStores';

import { HOTJAR_TEAM_MEMBER_ACCESS } from '../../../constants/hotjar';
import { MODAL_NAME, OVERLAY_MODAL } from '../../../constants/modals';
import type { IJob } from '../../../stores/JobStore/Job';
import { sendHotjarTag } from '../../../utils/hotjar';
import Skeleton from '../../common/Skeleton';
import { JobSectionLayout } from '../JobSectionLayout';

import JobActionMenu from './JobActionMenu';
import JobModal from './JobModal';
import JobStatDetail, { JobStatDetailSkeleton } from './JobStatDetail';
import { JobTitleHeadingContent } from './JobTitleHeadingContent';
import NotificationPreferenceModal from './NotificationPreferenceModal';
import PendingAlert from './PendingAlert';
import TeamMemberAccessModal from './TeamMemberAccessModal';
import {
  getNumberOfApplicationsInBucket,
  JobAction,
  triggerJobAction,
} from './actions';
import { navigateToUrl } from './utils';

interface Props {
  job: IJob;
  isRecommendedCandidatesTabShown: boolean;
  openRelevantApplicationDialog: () => void;
  freemiumButton: JSX.Element | null;
  addCustomError: PageConfig['dataDogRum']['addCustomError'];
}

const JobSection: React.FC<Props> = ({
  job,
  isRecommendedCandidatesTabShown,
  openRelevantApplicationDialog,
  freemiumButton,
  addCustomError,
}) => {
  const {
    uiStore: { modalStore },
    isLoading: storeIsLoading,
    jobStore: { isLoading = true },
    seekAdvertiserId,
    jobApplicationStore,
  } = useStores();

  const [, setQueryParams] = useQueryParamsStore();
  const [isJobDialogOpen, setJobDialogIsOpen] = React.useState(false);
  const [isExpireModalOpen, setIsExpireModalOpen] = React.useState(false);

  const { router, site } = useAppConfig();

  const onAction = (jobAction: JobAction) => {
    modalStore!.removeModal(MODAL_NAME.JOB_ACTIONS);

    if (jobAction === JobAction.VIEW_AD_INFORMATION) {
      setJobDialogIsOpen(true);
    }

    if (jobAction === JobAction.EXPIRE) {
      trackExpireAdClickEvent({
        prospectInBucketCount: getNumberOfApplicationsInBucket(job),
      });
      setIsExpireModalOpen(true);
      return;
    }

    if (jobAction === JobAction.NOTIFICATION_PREFERENCE) {
      trackViewJobNotificationsClickEvent();
      setQueryParams.displayOverlayModal(OVERLAY_MODAL.USER_PREFERENCES);
      return;
    }

    if (jobAction === JobAction.TEAM_MEMBER_ACCESS) {
      trackViewTeamAccessClickEvent();
      sendHotjarTag(HOTJAR_TEAM_MEMBER_ACCESS);
      modalStore!.addModal(MODAL_NAME.TEAM_MEMBER_ACCESS);
      return;
    }

    if (
      jobAction === JobAction.TALENT_SEARCH &&
      job?.jobInformation?.jobId !== null &&
      !isRecommendedCandidatesTabShown
    ) {
      navigateToUrl(
        router.talentSearchForJob({
          jobId: job.jobInformation.jobId,
          seekAdvertiserId,
        }),
        true,
      );
    }

    triggerJobAction(jobAction, job, router, site, addCustomError);
  };

  return (
    <JobSectionLayout
      heading={
        <Skeleton loading={storeIsLoading} numberOfCharacters={35}>
          <JobTitleHeadingContent job={job} />
        </Skeleton>
      }
      details={
        storeIsLoading ? (
          <JobStatDetailSkeleton />
        ) : (
          <JobStatDetail
            status={job.jobInformation.status}
            numberOfDaysRemaining={job.jobInformation.numberOfDaysRemaining}
            billableApplicants={job.jobInformation.billableApplicants}
            internalReference={job.jobInformation.internalReference}
            bucketStatsTotalCount={jobApplicationStore.bucketStats.totalCount}
            openRelevantApplicationDialog={openRelevantApplicationDialog}
          />
        )
      }
      jobAction={
        !isLoading ? (
          <JobActionMenu
            job={job}
            isRecommendedCandidatesTabShown={isRecommendedCandidatesTabShown}
            onAction={onAction}
          />
        ) : null
      }
      alert={
        job.isPending && (
          <Box paddingY="medium">
            <PendingAlert />
          </Box>
        )
      }
      modals={
        <>
          <JobModal
            job={job}
            isJobDialogOpen={isJobDialogOpen}
            onClose={() => setJobDialogIsOpen(false)}
          />
          {modalStore!.modals.includes(OVERLAY_MODAL.USER_PREFERENCES) && (
            <NotificationPreferenceModal modalStore={modalStore} job={job} />
          )}
          {modalStore!.modals.includes(MODAL_NAME.TEAM_MEMBER_ACCESS) && (
            <TeamMemberAccessModal />
          )}

          <ExpireJobDialog
            open={isExpireModalOpen}
            onClose={() => setIsExpireModalOpen(false)}
            expireJob={() => job.expire(addCustomError)}
            isCsView={job.userStore.user.isCsView}
            expireDate={job.expiryDate}
            updateExpiryDate={(expiryDate) =>
              job.updateExpiryDate(expiryDate, addCustomError)
            }
          />
        </>
      }
      freemiumButton={freemiumButton}
    />
  );
};

export default observer(JobSection);
